import { useCallback, useState } from "react";
import type { CreateServiceRequest } from "@/types/api";
import { KafkaFields } from "./KafkaFields";

function addIfPresent(
  payload: Record<string, string>,
  key: string,
  value: string,
) {
  if (value) payload[key] = value;
}

function buildKafkaPayload(fields: {
  bootstrapServers: string;
  groupId: string;
  securityProtocol: string;
  saslMechanism: string;
  saslUsername: string;
  saslPassword: string;
  sslCaCert: string;
  sslClientCert: string;
  sslClientKey: string;
}): Partial<CreateServiceRequest> {
  const payload: Record<string, string> = {
    "bootstrap.servers": fields.bootstrapServers,
  };
  addIfPresent(payload, "group.id", fields.groupId);
  addIfPresent(payload, "security.protocol", fields.securityProtocol);

  if (fields.securityProtocol.startsWith("SASL")) {
    addIfPresent(payload, "sasl.mechanism", fields.saslMechanism);
    addIfPresent(payload, "sasl.username", fields.saslUsername);
    addIfPresent(payload, "sasl.password", fields.saslPassword);
  }

  const usesSsl =
    fields.securityProtocol === "SSL" || fields.securityProtocol === "SASL_SSL";
  if (usesSsl) {
    addIfPresent(payload, "ssl.ca.content", fields.sslCaCert);
    addIfPresent(payload, "ssl.certificate.content", fields.sslClientCert);
    addIfPresent(payload, "ssl.key.content", fields.sslClientKey);
  }

  return payload;
}

export function useKafkaFields() {
  const [bootstrapServers, setBootstrapServers] = useState("");
  const [groupId, setGroupId] = useState("");
  const [securityProtocol, setSecurityProtocol] = useState("");
  // PLAIN is the working default for username/password SASL; an unset
  // mechanism would fall back to librdkafka's GSSAPI (Kerberos) and silently
  // ignore the credentials.
  const [saslMechanism, setSaslMechanism] = useState("PLAIN");
  const [saslUsername, setSaslUsername] = useState("");
  const [saslPassword, setSaslPassword] = useState("");
  const [sslCaCert, setSslCaCert] = useState("");
  const [sslClientCert, setSslClientCert] = useState("");
  const [sslClientKey, setSslClientKey] = useState("");

  const toPayload = useCallback(
    () =>
      buildKafkaPayload({
        bootstrapServers,
        groupId,
        securityProtocol,
        saslMechanism,
        saslUsername,
        saslPassword,
        sslCaCert,
        sslClientCert,
        sslClientKey,
      }),
    [
      bootstrapServers,
      groupId,
      securityProtocol,
      saslMechanism,
      saslUsername,
      saslPassword,
      sslCaCert,
      sslClientCert,
      sslClientKey,
    ],
  );

  const fields = (
    <KafkaFields
      bootstrapServers={bootstrapServers}
      setBootstrapServers={setBootstrapServers}
      groupId={groupId}
      setGroupId={setGroupId}
      securityProtocol={securityProtocol}
      setSecurityProtocol={setSecurityProtocol}
      saslMechanism={saslMechanism}
      setSaslMechanism={setSaslMechanism}
      saslUsername={saslUsername}
      setSaslUsername={setSaslUsername}
      saslPassword={saslPassword}
      setSaslPassword={setSaslPassword}
      sslCaCert={sslCaCert}
      setSslCaCert={setSslCaCert}
      sslClientCert={sslClientCert}
      setSslClientCert={setSslClientCert}
      sslClientKey={sslClientKey}
      setSslClientKey={setSslClientKey}
    />
  );

  return { fields, toPayload };
}
